/**
 * Design Export Utilities
 * Exports high-resolution PNG files from the Konva canvas for print production
 */
import { logger } from './logger';

/**
 * Export a design from a Konva stage as a high-resolution PNG base64 string
 * @param {Object} stageRef - React ref to the Konva Stage
 * @param {Object} options - Export options
 * @returns {string|null} - Base64 PNG data URL or null if no design
 */
export const exportDesignFromStage = (stageRef, options = {}) => {
  if (!stageRef || !stageRef.current) {
    return null;
  }

  const stage = stageRef.current;
  const layer = stage.getLayers()[0];
  
  if (!layer || layer.getChildren().length === 0) {
    return null;
  }

  const {
    pixelRatio = 4, // Higher ratio = higher DPI for print (300 DPI recommended)
    mimeType = 'image/png',
    quality = 1,
  } = options;

  try {
    // Export the entire stage as PNG
    const dataUrl = stage.toDataURL({
      pixelRatio,
      mimeType,
      quality,
    });
    
    return dataUrl;
  } catch (error) {
    logger.error('Error exporting design:', error);
    return null;
  }
};

/**
 * Export all designs from a DesignCanvas component
 * @param {Object} canvasRef - Ref to DesignCanvas component with getStageRef method
 * @param {Object} options - Export options
 * @returns {string|null} - Base64 PNG data URL or null if no design
 */
export const exportFromDesignCanvas = (canvasRef, options = {}) => {
  if (!canvasRef || !canvasRef.current) {
    return null;
  }

  // DesignCanvas exposes getStageRef method
  if (typeof canvasRef.current.getStageRef === 'function') {
    const stageRef = canvasRef.current.getStageRef();
    return exportDesignFromStage({ current: stageRef }, options);
  }

  return null;
};

/**
 * Export designs from all views
 * @param {Object} canvasRefs - Object with refs to each view's canvas {front, back, left, right}
 * @param {Object} options - Export options
 * @returns {Object} - Object with base64 data for each view {front_design_data, back_design_data, ...}
 */
export const exportAllDesigns = async (canvasRefs, options = {}) => {
  const exports = {
    front_design_data: null,
    back_design_data: null,
    left_design_data: null,
    right_design_data: null,
  };

  const views = ['front', 'back', 'left', 'right'];
  
  for (const view of views) {
    const ref = canvasRefs[view];
    if (ref && ref.current) {
      const data = exportFromDesignCanvas(ref, options);
      if (data) {
        exports[`${view}_design_data`] = data;
      }
    }
  }

  return exports;
};

/**
 * Check if any designs exist in the exported data
 * @param {Object} exports - Exported design data
 * @returns {boolean}
 */
export const hasExportedDesigns = (exports) => {
  return Object.values(exports).some(data => data !== null);
};

/**
 * Get list of views that have designs
 * @param {Object} exports - Exported design data
 * @returns {string[]} - Array of view names with designs
 */
export const getViewsWithDesigns = (exports) => {
  const views = [];
  if (exports.front_design_data) views.push('front');
  if (exports.back_design_data) views.push('back');
  if (exports.left_design_data) views.push('left');
  if (exports.right_design_data) views.push('right');
  return views;
};

/**
 * Calculate print placement string from exports and/or raw design elements
 * @param {Object} exports - Exported design data (base64 images)
 * @param {Object} rawDesigns - Raw design element arrays (fallback)
 * @returns {string} - 'front', 'back', 'both', 'all', 'left', 'right', or 'custom'
 */
export const calculatePrintPlacement = (exports, rawDesigns = null) => {
  // First try to get views from exports
  let views = getViewsWithDesigns(exports);
  
  // If no exports, fall back to raw design element counts
  if (views.length === 0 && rawDesigns) {
    if (rawDesigns.front?.length > 0) views.push('front');
    if (rawDesigns.back?.length > 0) views.push('back');
    if (rawDesigns.left?.length > 0) views.push('left');
    if (rawDesigns.right?.length > 0) views.push('right');
  }
  
  if (views.length === 0) return 'front'; // Default to front if no designs
  if (views.length === 1) return views[0];
  if (views.length === 2 && views.includes('front') && views.includes('back')) return 'both';
  if (views.length === 4) return 'all';
  return 'custom';
};

export default {
  exportDesignFromStage,
  exportFromDesignCanvas,
  exportAllDesigns,
  hasExportedDesigns,
  getViewsWithDesigns,
  calculatePrintPlacement,
};
